// hooks/useCynths.js
import { useReadContract, useWriteContract, useWaitForTransactionReceipt, usePublicClient } from 'wagmi';
import { writeContract } from 'viem/actions';
import { BigNumber, ethers } from 'ethers';
import { useAppKitAccount } from '@reown/appkit/react';
import Swal from 'sweetalert2';
import {
  cynthsAddress,
  erc20Address,
  provider,
  cranniAddress,
} from '../.config';
import cynthAbi from '../abi/Cynths.json';
import erc20abi from '../abi/ERC20_ABI.json';
import { maxUint256 } from 'viem';

export const useCynths = () => {
  const { address } = useAppKitAccount();


  const handleTransaction = async (txFunc) => {
    try {
      const tx = await txFunc();
      if (tx.wait) await tx.wait();
      return tx;
    } catch (error) {
      console.error('Transaction error:', error);
      throw error;
    }
  };

  // ---------------------------
  //  (READ)
  // ---------------------------

  // currentFee
  const currentFeeHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'currentFee',
    enabled: false,
  });
  const getCurrentFee = async () => {
    const res = await currentFeeHook.refetch();
    return res.data;
  };

  // balanceOf Cranni
  const cranniBalanceHook = useReadContract({
    address: cranniAddress,
    abi: erc20abi.abi,
    functionName: 'balanceOf',
    args: [],
    enabled: false,
  });
  const getCranniBalance = async (accountAddr) => {
    const contract = new ethers.Contract(cranniAddress, erc20abi.abi, provider);
    const cranniBalance = await contract.balanceOf(accountAddr);
    return cranniBalance;
  };

  // getLatestPrice
  const tokenPriceRead = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'getLatestPrice',
    args: [],
    enabled: false,
  });

  const getTokenPrice = async (ticker) => {
    try {
      const contract = new ethers.Contract(cynthsAddress, cynthAbi, provider);
      const price = await contract.getLatestPrice(ticker);
      return parseFloat(ethers.utils.formatUnits(price, 6)).toFixed(2);
    } catch (error) {
      console.error("Error in getTokenPrice:", error);
      return "0.00";
    }
  };

  // getTotalMinted
  const totalMintedHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'getTotalMinted',
    enabled: false,
  });
  const getTotalMinted = async () => {
    const res = await totalMintedHook.refetch();
    return parseFloat(ethers.utils.formatUnits(res.data, 6)).toFixed(2);
  };

  // getTotalSupply
  const totalSupplyHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'getTotalSupply',
    enabled: false,
  });
  const getTotalSupply = async () => {
    const res = await totalSupplyHook.refetch();
    return parseFloat(ethers.utils.formatUnits(res.data, 6)).toFixed(2);
  };

  // getTokens
  const tokensHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'getTokens',
    enabled: false,
  });
  const getTokenList = async () => {
    const res = await tokensHook.refetch();
    return res.data.map((token) => ({
      ticker: ethers.utils.parseBytes32String(token.ticker),
      tokenAddress: token.tokenAddress,
      category: token.category,
      description: token.description,
    }));
  };

  // previewMintAmount
  const previewMintHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'previewMintAmount',
    enabled: false,
  });
  const previewMint = async (ticker, amountUSD) => {

    const weiAmount = ethers.utils.parseUnits(amountUSD.toString(), 6);
    const contract = new ethers.Contract(cynthsAddress, cynthAbi, provider);

    const res = await contract.previewMintAmount(ticker, weiAmount);
    // console.log(res);
    return ethers.utils.formatUnits(res, 18);
  };

  // previewReturnOnBurn
  const previewBurnHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'previewReturnOnBurn',
    enabled: false,
  });
  const previewBurn = async (ticker, amountSynth) => {
    let amountSynthNum = typeof amountSynth === 'string' ? parseFloat(amountSynth) : amountSynth;
    let amountSynthStr = amountSynthNum.toFixed(18);
    let weiAmount = ethers.utils.parseUnits(amountSynthStr, 18);
    console.log(weiAmount);
    const contract = new ethers.Contract(cynthsAddress, cynthAbi, provider);
    const res = await contract.previewReturnOnBurn(ticker, weiAmount);
    return ethers.utils.formatUnits(res, 18);
  };

  // getBoldPrice
  const boldPriceHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'getBoldPrice',
    enabled: false,
  });
  const getBoldPrice = async () => {
    const res = await boldPriceHook.refetch();
    return parseFloat(ethers.utils.formatUnits(res.data, 6)).toFixed(2);
  };

  // getSdrPrice
  const sdrPriceHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'getSdrPrice',
    enabled: false,
  });
  const getSdrPrice = async () => {
    const res = await sdrPriceHook.refetch();
    return parseFloat(ethers.utils.formatUnits(res.data, 6)).toFixed(2);
  };

  // getMuskPrice
  const muskPriceHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'getMuskPrice',
    enabled: false,
  });
  const getMuskPrice = async () => {
    const res = await muskPriceHook.refetch();
    return parseFloat(ethers.utils.formatUnits(res.data, 6)).toFixed(2);
  };

  // getPrivPrice
  const privPriceHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'getPrivPrice',
    enabled: false,
  });
  const getPrivPrice = async () => {
    const res = await privPriceHook.refetch();
    return parseFloat(ethers.utils.formatUnits(res.data, 6)).toFixed(2);
  };

  // btcWeight
  const btcWeightHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'btcWeight',
    enabled: false,
  });
  const getBtcWeight = async () => {
    const res = await btcWeightHook.refetch();
    return res.data + '%';
  };

  // goldWeight
  const goldWeightHook = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'goldWeight',
    enabled: false,
  });
  const getGoldWeight = async () => {
    const res = await goldWeightHook.refetch();
    return res.data + '%';
  };

  // getSDRBasket (para USD, CNY, YEN, GBP, EUR)
  const sdrBasketHookUSD = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'sdrUSD',
    enabled: false,
  });
  const sdrBasketHookCNY = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'sdrCNY',
    enabled: false,
  });
  const sdrBasketHookYEN = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'sdrYEN',
    enabled: false,
  });
  const sdrBasketHookGBP = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'sdrGBP',
    enabled: false,
  });
  const sdrBasketHookEUR = useReadContract({
    address: cynthsAddress,
    abi: cynthAbi,
    functionName: 'sdrEUR',
    enabled: false,
  });
  const getSdrBasket = async () => {
    const [usdRes, cnyRes, yenRes, gbpRes, eurRes] = await Promise.all([
      sdrBasketHookUSD.refetch(),
      sdrBasketHookCNY.refetch(),
      sdrBasketHookYEN.refetch(),
      sdrBasketHookGBP.refetch(),
      sdrBasketHookEUR.refetch(),
    ]);
    const formatSdrValue = (value) => {
      const bigNumberValue = ethers.BigNumber.from(value);
      return bigNumberValue.div(ethers.BigNumber.from(10).pow(13)).toNumber() / 100000;
    };
    return {
      USD: formatSdrValue(usdRes.data),
      CNY: formatSdrValue(cnyRes.data),
      YEN: formatSdrValue(yenRes.data),
      GBP: formatSdrValue(gbpRes.data),
      EUR: formatSdrValue(eurRes.data),
    };
  };

  const formatCurrency = (value, currency) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 5,
    }).format(value);

  // ---------------------------
  // (WRITE)
  // ---------------------------

  const fetchAllowanceHook = useReadContract({
    address: erc20Address,
    abi: erc20abi.abi,
    functionName: 'allowance',
    args: [address, cynthsAddress], // Initial args
    enabled: true
  });


  const checkAllowance = async (owner, spender) => {
    try {
      if (!owner || !spender) {
        throw new Error("Missing required addresses");
      }

      const allowance = await fetchAllowanceHook.refetch({
        args: [owner, spender],
      });

      console.log("ALLOWANCE DATA: ", allowance);

      return ethers.BigNumber.from(allowance.data || '0');
    } catch (error) {
      console.error("Allowance check failed:", error);
      return ethers.BigNumber.from('0');
    }
  };

  const publicClient = usePublicClient();
  const { writeContractAsync } = useWriteContract();

  const showTransactionDialog = async (transactionPromise, messages) => {
    try {
      // Show loading dialog
      Swal.fire({
        title: messages.pending.title,
        html: messages.pending.text,
        icon: 'info',
        showConfirmButton: false,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });

      // Execute transaction
      const hash = await transactionPromise;

      // Wait for blockchain confirmation
      const receipt = await publicClient.waitForTransactionReceipt({
        hash,
        confirmations: 2
      });

      // Handle transaction result
      if (receipt.status !== "success") {
        throw new Error("Transaction failed on chain");
      }

      // Close loading dialog
      Swal.close();

      // Show success notification
      Swal.fire({
        title: messages.success.title,
        text: messages.success.text,
        icon: 'success',
        timer: 2500,
        showConfirmButton: false
      });

      return receipt;
    } catch (error) {
      // Close current dialog
      Swal.close();

      // Show error message
      Swal.fire({
        title: 'Transaction Failed',
        html: `<div class="text-red-300">${error.message}</div>`,
        icon: 'error',
        confirmButtonText: 'OK'
      });

      throw error;
    }
  };

  const mint = async (ticker, amountUSD) => {
    try {
      // Validate inputs
      if (!erc20Address || !cynthsAddress || !address) {
        throw new Error("Contract addresses not initialized");
      }

      // Convert amount to wei
      const weiAmount = ethers.utils.parseUnits(amountUSD.toString(), 6);

      // Check allowance
      const allowance = await checkAllowance(
        address,
        cynthsAddress
      );

      console.log("allowance: ", allowance);

      if (allowance.lt(weiAmount)) {
        // Approval needed
        const approvePromise = writeContractAsync({
          address: erc20Address,
          abi: erc20abi.abi,
          functionName: 'approve',
          args: [cynthsAddress, maxUint256],
        });

        // Show approval dialog
        const approvalResult = await showTransactionDialog(approvePromise, {
          pending: {
            title: 'Approving',
            text: 'Waiting for approval confirmation...',
          },
          success: {
            title: 'Approved',
            text: 'Approval successful!',
          },
        });

        if (!approvalResult) {
          throw new Error("Approval failed");
        }
      }

      // Mint tokens
      const mintPromise = writeContractAsync({
        address: cynthsAddress,
        abi: cynthAbi,
        functionName: 'mintSynth',
        args: [ticker, weiAmount],
      });

      // Show mint dialog
      const mintResult = await showTransactionDialog(mintPromise, {
        pending: {
          title: 'Minting',
          text: 'Waiting for mint confirmation...',
        },
        success: {
          title: 'Minted',
          text: 'Mint successful!',
        },
      });

      return mintResult;
    } catch (error) {
      console.error("Mint process failed:", error);
      throw error;
    }
  };

  const burn = async (ticker, amountSynth) => {
    try {
      // Validate inputs
      if (!erc20Address || !cynthsAddress || !address) {
        throw new Error("Contract addresses not initialized");
      }

      // Convert amount to wei
      const weiAmount = ethers.utils.parseUnits(amountSynth.toString(), 18);

      // Get token data
      const tokens = await getTokenList();
      const tokenData = tokens.find((token) => token.ticker === ticker);

      if (!tokenData) {
        throw new Error(`Token with ticker ${ticker} not found`);
      }

      // Check allowance

      const contract = new ethers.Contract(tokenData.tokenAddress, erc20abi.abi, provider);

      const allowance = await contract.allowance(address, cynthsAddress);

      /*
      const allowance = await checkAllowance(
        tokenData.tokenAddress,
        cynthsAddress
      ); */

      if (allowance.lt(weiAmount)) {
        // Approval needed
        const approvePromise = writeContractAsync({
          address: tokenData.tokenAddress,
          abi: erc20abi.abi,
          functionName: 'approve',
          args: [cynthsAddress, maxUint256],
        });

        // Show approval dialog
        const approvalResult = await showTransactionDialog(approvePromise, {
          pending: {
            title: 'Approving',
            text: 'Waiting for approval confirmation...',
          },
          success: {
            title: 'Approved',
            text: 'Approval successful!',
          },
        });

        if (!approvalResult) {
          throw new Error("Approval failed");
        }
      }

      // Burn tokens
      const burnPromise = writeContractAsync({
        address: cynthsAddress,
        abi: cynthAbi,
        functionName: 'burnSynth',
        args: [ticker, weiAmount],
      });

      // Show burn dialog
      const burnResult = await showTransactionDialog(burnPromise, {
        pending: {
          title: 'Burning',
          text: 'Waiting for burn confirmation...',
        },
        success: {
          title: 'Burned',
          text: 'Burn successful!',
        },
      });

      return burnResult;
    } catch (error) {
      console.error("Burn process failed:", error);
      throw error;
    }
  };

  return {
    mint,
    burn,
    getTokenPrice,
    getTotalMinted,
    getTotalSupply,
    getTokenList,
    previewMint,
    previewBurn,
    getBoldPrice,
    getSdrPrice,
    getMuskPrice,
    getPrivPrice,
    getBtcWeight,
    getGoldWeight,
    getSdrBasket,
    formatCurrency,
    getCurrentFee,
    getCranniBalance,
  };
};